import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import * as E from '@react-email/components'
import {
	json,
	redirect,
	type ActionFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Link, useFetcher } from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { Separator } from '#app/components/ui/separator.js'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { EmailSchema, UsernameSchema } from '#app/utils/user-validation.ts'
import { prepareVerification } from './verify.server.ts'

const ForgotPasswordSchema = z.object({
	usernameOrEmail: z.union([EmailSchema, UsernameSchema]),
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: ForgotPasswordSchema.superRefine(async (data, ctx) => {
			const user = await prisma.user.findFirst({
				where: {
					OR: [
						{ email: data.usernameOrEmail },
						{ username: data.usernameOrEmail },
					],
				},
				select: { id: true },
			})
			if (!user) {
				ctx.addIssue({
					path: ['usernameOrEmail'],
					code: z.ZodIssueCode.custom,
					message: 'No user exists with this username or email',
				})
				return
			}
		}),
		async: true,
	})
	if (submission.status !== 'success') {
		return json(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}
	const { usernameOrEmail } = submission.value

	const user = await prisma.user.findFirstOrThrow({
		where: { OR: [{ email: usernameOrEmail }, { username: usernameOrEmail }] },
		select: { email: true, username: true, name: true },
	})
	const { verifyUrl, redirectTo, otp } = await prepareVerification({
		period: 60 * 60 * 24 * 365,
		request,
		type: 'reset-password',
		target: usernameOrEmail,
	})
	const response = await sendEmail({
		to: user.email,
		subject: `Kogia | Forgot Password`,
		react: (
			<ForgotPasswordEmail
				name={(user.name || user.username) as string}
				onboardingUrl={verifyUrl.toString()}
				otp={otp}
			/>
		),
	})
	if (response.status === 'success') {
		return redirect(redirectTo.toString())
	} else {
		return json(
			{ result: submission.reply({ formErrors: [response.error.message] }) },
			{ status: 500 },
		)
	}
}

function ForgotPasswordEmail({
	name,
	onboardingUrl,
	otp,
}: {
	name: string
	onboardingUrl: string
	otp: string
}) {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Head />
			<E.Preview>Forgot your password?</E.Preview>
			<E.Body>
				<E.Container>
					<E.Heading as="h1">Forgot your password?</E.Heading>
					<E.Text>Dear {name},</E.Text>
					<E.Text>
						We heard you forgot your password! No worries, you can easily reset
						it.
					</E.Text>
					<E.Text>
						Here is your verification code:{' '}
						<E.Text className="bold">{otp}</E.Text>
					</E.Text>
					<E.Text>
						Or you can follow the link found here{' '}
						<E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
					</E.Text>
					<E.Text>All the best,</E.Text>
					<E.Text>The Kogia Team</E.Text>
				</E.Container>
			</E.Body>
		</E.Html>
	)
}

export const meta: MetaFunction = () => {
	return [{ title: 'Kogia | Forgot Password' }]
}

export default function ForgotPasswordRoute() {
	const forgotPassword = useFetcher<typeof action>()

	const [form, fields] = useForm({
		id: 'forgot-password-form',
		constraint: getZodConstraint(ForgotPasswordSchema),
		lastResult: forgotPassword.data?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ForgotPasswordSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="flex w-full max-w-screen-md flex-col gap-3 p-4 md:p-8">
			<div className="flex items-center gap-4">
				<h2 className="font-serif text-h2">Forgot Password?</h2>
				<Separator className="flex-1" />
			</div>
			<p className="text-body-md text-muted-foreground">
				No worries, we'll send you reset instructions.
			</p>
			<forgotPassword.Form method="POST" {...getFormProps(form)}>
				<HoneypotInputs />
				<div>
					<Field
						labelProps={{
							htmlFor: fields.usernameOrEmail.id,
							children: 'Username or Email',
						}}
						inputProps={{
							autoFocus: true,
							className: 'mt-1',
							...getInputProps(fields.usernameOrEmail, { type: 'text' }),
						}}
						errors={fields.usernameOrEmail.errors}
					/>
				</div>
				<ErrorList errors={form.errors} id={form.errorId} />

				<div className="mt-6">
					<StatusButton
						className="w-full"
						status={
							forgotPassword.state === 'submitting'
								? 'pending'
								: form.status ?? 'idle'
						}
						type="submit"
						disabled={forgotPassword.state !== 'idle'}
					>
						Recover password
					</StatusButton>
				</div>
			</forgotPassword.Form>
			<Link to="/login" className="mt-11 text-center text-body-sm font-bold">
				Back to Login
			</Link>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
